/* eslint-disable max-len */
import {inputType} from '../../../../../pages/esgManager/interface/inputType';

export const pillers = {
  generalGovernance: 'pillar_governance',
  generalStrategy: 'pillar_strategy',
  generalRiskManagement: 'pillar_risk_management',
  generalMetricsTargets: 'pillar_metrics_targets'
};

export const GENERAL_FIELD_NAMES = {
  GQ1_Q1_VALUE: 'gquQg1Q1_value',
  GQ1_Q2_VALUE: 'gquQg1Q2_value',
  GQ2_Q1_VALUE: 'gquQg2Q1_value',
  GQ2_Q2_VALUE: 'gquQg2Q2_value',
  GQ2_Q3_VALUE: 'gquQg2Q3_value',
  GQ3_Q1_VALUE: 'gquQg3Q1_value',
  GQ3_Q2_VALUE: 'gquQg3Q2_value',
  GQ3_Q3_VALUE: 'gquQg3Q3_value',
  GQ4_Q1_VALUE: 'gquQg4Q1_value',
  GQ4_Q2_VALUE: 'gquQg4Q2_value',
  GQ4_Q3_VALUE: 'gquQg4Q3_value'
} as const;

export const GeneralQuestions = {
  QUESTION_GROUP_1: {
    KEY: '1',
    NAME: 'The focus is on how the organization oversees and manages climate-related issues.',
    QUESTIONS: {
      QUESTION_1: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ1_Q1_VALUE,
        fieldLabel: '',
        Question:
          'a. Describe the board’s oversight of climate-related risks and opportunities.',
        tooltip:
          'This involves explaining how the board considers climate issues when reviewing strategy, major plans, and monitoring progress against goals.'
      },
      QUESTION_2: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ1_Q2_VALUE,
        fieldLabel: '',
        Question:
          'b. Describe management’s role in assessing and managing climate-related risks and opportunities.',
        tooltip:
          'This includes whether there are specific management-level positions or committees responsible for climate issues and how they report to the board.'
      }
    }
  },

  QUESTION_GROUP_2: {
    KEY: '2',
    NAME: `The focus is on the actual and potential impacts of 
    climate change on the organization's business, strategy, and financial planning.`,
    QUESTIONS: {
      QUESTION_1: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ2_Q1_VALUE,
        fieldLabel: '',
        Question:
          'a) Describe the climate-related risks and opportunities the organization has identified over the short, medium, and long term.',
        tooltip:
          'This requires the company to define these time horizons and identify specific physical risks (e.g., floods, heatwaves) and transition risks (e.g., policy changes, new technologies).'
      },
      QUESTION_2: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ2_Q2_VALUE,
        fieldLabel: '',
        Question:
          'b) Describe the impact of climate-related risks and opportunities on the organization’s businesses, strategy, and financial planning.',
        tooltip: `This should explain how the identified risks and opportunities are expected 
          to affect the company's products, services, supply chain, and capital allocation.`
      },
      QUESTION_3: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ2_Q3_VALUE,
        fieldLabel: '',
        Question:
          'c) Describe the resilience of the organization’s strategy, taking into consideration different climate-related scenarios, including a 2 °C or lower scenario.',
        tooltip: `This is a forward-looking analysis to assess how the company's strategy might 
          perform under various future climate states, such as a rapid transition to a low-carbon economy or a failure to meet global climate targets.`
      }
    }
  },

  QUESTION_GROUP_3: {
    KEY: '3',
    NAME: 'The focus is on how the organization identifies, assesses, and manages its climate-related risks.',
    QUESTIONS: {
      QUESTION_1: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ3_Q1_VALUE,
        fieldLabel: '',
        Question:
          'a) Describe the organization’s processes for identifying and assessing climate-related risks.',
        tooltip:
          'This details the methods used to determine the relative significance of different climate-related risks.'
      },
      QUESTION_2: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ3_Q2_VALUE,
        fieldLabel: '',
        Question:
          'b) Describe the organization’s processes for managing climate-related risks.',
        tooltip:
          'This explains how the company makes decisions to mitigate, transfer, accept, or control its identified climate risks.'
      },
      QUESTION_3: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ3_Q3_VALUE,
        fieldLabel: '',
        Question:
          'c) Describe how processes for identifying, assessing, and managing climate-related risks are integrated into the organization’s overall risk management.',
        tooltip:
          'This demonstrates whether climate risk is treated as a standalone issue or is embedded within the company’s existing enterprise-wide risk management framework.'
      }
    }
  },

  QUESTION_GROUP_4: {
    KEY: '4',
    NAME: 'The focus is on the specific data used to assess and manage relevant climate-related risks and opportunities.',
    QUESTIONS: {
      QUESTION_1: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ4_Q1_VALUE,
        fieldLabel: '',
        Question:
          'a) Disclose the metrics used by the organization to assess climate-related risks and opportunities in line with its strategy and risk management process.',
        tooltip:
          'These are the specific key performance indicators (KPIs) the company uses to measure its climate-related performance.'
      },
      QUESTION_2: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ4_Q2_VALUE,
        fieldLabel: '',
        Question:
          'b) Disclose Scope 1, Scope 2, and, if appropriate, Scope 3 greenhouse gas (GHG) emissions, and the related risks.',
        tooltip: `This requires quantifying the company's direct emissions (Scope 1), 
          emissions from purchased energy (Scope 2), and value chain emissions (Scope 3), which are often the largest component.`
      },
      QUESTION_3: {
        fieldType: inputType.TEXTAREA,
        fieldName: GENERAL_FIELD_NAMES.GQ4_Q3_VALUE,
        fieldLabel: '',
        Question:
          'c) Describe the targets used by the organization to manage climate-related risks and opportunities and performance against targets.',
        tooltip:
          'This includes any targets for GHG emissions reduction, water usage, or energy efficiency, and reporting on progress made towards achieving them.'
      }
    }
  }
} as const;
